/**
 * Resolves a cart mutation error into what the shopper should read.
 *
 * Errors coming out of the cart hooks/actions may carry a stable
 * `errorCode` (outOfStock, insufficientInventory, productAlreadyInCartAsSale…)
 * and optional `errorParams` (e.g. `{ count }`), set by
 * lib/util/medusa-error from the backend's `ERR_<code>:` convention. This
 * helper turns that into a localized description (+ an optional hint line)
 * and falls back to the raw message only when there is no translation.
 *
 * `t` is a next-intl translator; `errorsKey` is the path to the
 * `cart.errors` object relative to that translator's namespace
 * ("cart.errors" for a root translator, "errors" for useTranslations('cart')).
 */

export type CartErrorLike = {
  message?: string
  errorCode?: string
  errorParams?: Record<string, string | number>
}

export type Translator = ((key: string, values?: Record<string, string | number>) => string) & {
  has?: (key: string) => boolean
}

export interface ResolvedCartError {
  /** Main line shown to the shopper. */
  description: string
  /** Optional second line ("Try again later or pick another product"). */
  hint?: string
  /** True when the description came from a translation, not the raw message. */
  localized: boolean
}

/** Codes for which a "try later / pick another product" hint is useful. */
const HINTED_CODES = new Set(["outOfStock", "insufficientInventory"])

function tryTranslate(
  t: Translator,
  key: string,
  values?: Record<string, string | number>
): string | null {
  if (typeof t.has === "function" && !t.has(key)) return null
  try {
    const out = t(key, values)
    // next-intl returns the key path itself when the message is missing.
    if (!out || out === key || out.endsWith(`.${key}`)) return null
    return out
  } catch {
    return null
  }
}

export function resolveCartError(
  t: Translator,
  error: CartErrorLike | null | undefined,
  options: { errorsKey?: string; fallback: string }
): ResolvedCartError {
  const errorsKey = options.errorsKey ?? "cart.errors"
  const code = error?.errorCode
  const params = error?.errorParams

  if (code) {
    // insufficientInventory with a known count → "Only N left" variant.
    const count = params?.count
    const localized =
      (code === "insufficientInventory" && typeof count === "number"
        ? tryTranslate(t, `${errorsKey}.insufficientInventoryCount`, { count })
        : null) ?? tryTranslate(t, `${errorsKey}.${code}`, params)
    if (localized) {
      const hint = HINTED_CODES.has(code)
        ? tryTranslate(t, `${errorsKey}.tryLaterHint`) ?? undefined
        : undefined
      return { description: localized, hint, localized: true }
    }
  }

  return {
    description: error?.message || options.fallback,
    localized: false,
  }
}

/** One-string form for toasts: description + hint on a new line. */
export function cartErrorText(
  t: Translator,
  error: CartErrorLike | null | undefined,
  options: { errorsKey?: string; fallback: string }
): string {
  const r = resolveCartError(t, error, options)
  return r.hint ? `${r.description}\n${r.hint}` : r.description
}
